/**
 * Sanitize error messages to prevent information disclosure
 * @param {string} message - Original error message
 * @returns {string} Sanitized message
 */
export const sanitizeErrorMessage = (message) => {
	if (!message || typeof message !== 'string') {
		return 'An error occurred';
	}

	// Patterns that might contain sensitive information
	const sensitivePatterns = [
		// Database-related
		/database.*connection/gi,
		/sql.*error/gi,
		/connection.*string/gi,
		/server.*name/gi,

		// Authentication-related
		/password/gi,
		/token/gi,
		/secret/gi,
		/key/gi,
		/authentication.*failed.*user/gi,
		// System paths
		/[a-zA-Z]:\\[^\\]*\\[^\\]*/gi,
		/\/[a-zA-Z]+\/[a-zA-Z]+\/[a-zA-Z]+/gi,

		// IP addresses and ports
		/\b(?:\d{1,3}\.){3}\d{1,3}:\d+\b/gi,

		// Stack traces
		/at\s+[a-zA-Z.]+\s+\([^)]+\)/gi
	];

	let sanitized = message;

	// Replace sensitive patterns
	sensitivePatterns.forEach(pattern => {
		sanitized = sanitized.replace(pattern, '[REDACTED]');
	});

	// Common generic replacements
	const genericReplacements = {
		'Internal Server Error': 'A server error occurred. Please try again later.',
		'Connection timeout': 'Service temporarily unavailable. Please try again.',
		'Network Error': 'Unable to connect to server. Please check your connection.',
		'Request failed with status code 500': 'A server error occurred. Please try again later.',
		'Request failed with status code 503': 'Service temporarily unavailable. Please try again later.'
	};

	Object.entries(genericReplacements).forEach(([original, replacement]) => {
		if (sanitized.includes(original)) {
			sanitized = replacement;
		}
	});

	return sanitized;
};

/**
 * Get user-friendly error message based on status code
 * @param {number} statusCode - HTTP status code
 * @param {string} originalMessage - Original error message
 * @returns {string} User-friendly message
 */
export const getUserFriendlyError = (statusCode, originalMessage = '') => {
	const errorMap = {
		400: 'Invalid request. Please check your input and try again.',
		401: 'Authentication required. Please log in again.',
		403: 'Access denied. You don\'t have permission to perform this action.',
		404: 'Requested resource not found.',
		408: 'Request timeout. Please try again.',
		409: 'Conflict occurred. The resource may have been modified.',
		422: 'Invalid input data. Please check your entries.',
		429: 'Too many requests. Please wait before trying again.',
		500: 'Server error occurred. Please try again later.',
		502: 'Service temporarily unavailable. Please try again.',
		503: 'Service temporarily unavailable. Please try again later.',
		504: 'Request timeout. Please try again.'
	};

	return errorMap[statusCode] || sanitizeErrorMessage(originalMessage) || 'An unexpected error occurred.';
};

/**
 * Log error securely for monitoring
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export const logSecureError = (error, context = {}) => {
	const errorLog = {
		message: sanitizeErrorMessage(error.message),
		timestamp: new Date().toISOString(),
		userAgent: navigator.userAgent,
		url: window.location.href,
		userId: context.userId || 'anonymous',
		errorId: `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
		stack: error.stack ? sanitizeErrorMessage(error.stack) : null
	};

	if (import.meta.env.DEV) {
		console.error('Secure Error Log:', errorLog);
	} else {
		// Send to production logging service
		// Implement based on your logging infrastructure
	}

	return errorLog.errorId;
};
